import { isValidElement, ReactNode } from 'react';
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';
import {
  alertApiRef,
  AnyApiFactory,
  configApiRef,
  createApiFactory,
  createApiRef,
  discoveryApiRef,
  oauthRequestApiRef,
  BackstageIdentityApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import { OAuth2 } from '@backstage/core-app-api';
import { ToastApiMessage, toastApiRef } from '@backstage/frontend-plugin-api';
import { techRadarApiRef } from '@backstage-community/plugin-tech-radar';
import { MyTechRadarClient } from './lib/MyTechRadarClient';

// Generic OIDC auth against the MOH Keycloak realm. Backstage has no
// built-in apiRef for the generic `oidc` provider (unlike githubAuthApiRef
// etc.), so it's defined here — provider.id must be 'oidc' to match the
// backend provider id configured in app-config.yaml.
export const keycloakAuthApiRef = createApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
>({ id: 'auth.keycloak' });

// Backport of backstage/backstage#34112: register a ToastApi shim backed by
// AlertApi so components built for the new frontend system (e.g.
// NotificationsSidebarItem) don't crash with "No implementation available
// for apiRef{core.toast}". Remove once Backstage ships >=1.52 with the
// fix in @backstage/app-defaults.
const reactNodeToString = (node: ReactNode | null | undefined): string => {
  if (node === null || node === undefined) return '';
  if (typeof node === 'string') return node;
  if (Array.isArray(node)) return node.map(reactNodeToString).join(' ');
  if (isValidElement(node)) {
    return reactNodeToString(
      (node.props as { children?: ReactNode })?.children,
    );
  }
  return '';
};

const toastStatusToSeverity = (message: ToastApiMessage) => {
  switch (message.status ?? 'success') {
    case 'warning':
      return 'warning';
    case 'danger':
      return 'error';
    case 'success':
      return 'success';
    default:
      return 'info';
  }
};

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: keycloakAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      OAuth2.create({
        discoveryApi,
        oauthRequestApi,
        configApi,
        provider: {
          id: 'oidc',
          title: 'Keycloak',
          icon: () => null,
        },
        environment: configApi.getOptionalString('auth.environment'),
        defaultScopes: ['openid', 'profile', 'email'],
      }),
  }),
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  createApiFactory({
    api: techRadarApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) =>
      new MyTechRadarClient(
        configApi.getOptionalString('techRadar.dataUrl') ??
          '/tech-radar-data.json',
      ),
  }),
  createApiFactory({
    api: toastApiRef,
    deps: { alertApi: alertApiRef },
    factory: ({ alertApi }) => ({
      post(toast: ToastApiMessage) {
        alertApi.post({
          message: reactNodeToString(toast?.title),
          severity: toastStatusToSeverity(toast),
          display: 'transient',
        });
        // AlertApi.post() returns void with no id/handle, so there is no
        // way to dismiss a specific alert early through it. This is an
        // upstream limitation of this backport shim, not a bug in the
        // caller's usage of close() — remove this whole shim (see comment
        // above) once real ToastApi support lands and close() will work.
        return { close: () => {} };
      },
    }),
  }),
];
